import { columnIndexFromLetter } from './column-letter';

/**
 * A single image to embed into an Excel worksheet at export time. Used by
 * `writeOneSheet` / `buildExcelMultiSheet` — typical use case: signature
 * PNGs (employee + manager) stamped onto a weekly recap sheet (US-C).
 */
export interface SheetImage {
  /**
   * Target cell, either as a spreadsheet reference (`"L5"`) or as an
   * explicit `{ row, col }` pair. Both forms use the SAME 1-based
   * spreadsheet numbering (row 1 = first row, col 1 = column "A") for
   * consistency — this module is the only place that converts down to
   * ExcelJS's 0-based anchor coordinates.
   */
  cell: string | { row: number; col: number };
  /** PNG image data — accepts either a raw base64 string or a full `data:image/png;base64,...` URL (the prefix is stripped automatically). */
  base64: string;
  /** Rendered width in pixels. */
  width: number;
  /** Rendered height in pixels. */
  height: number;
}

/** Excel row heights are in points; image dimensions here are in px at 96 DPI (72 points/inch ÷ 96 px/inch). */
const POINTS_PER_PIXEL = 72 / 96;

/** Parse a `"L5"`-style reference into a 0-based `{ row, col }` pair. */
function parseCellString(ref: string): { row: number; col: number } {
  const match = /^([A-Za-z]+)(\d+)$/.exec(ref.trim());
  if (!match) {
    throw new Error(`Invalid cell reference for image: "${ref}" (expected e.g. "L5")`);
  }
  const [, colLetters, rowDigits] = match;
  return {
    row: Number(rowDigits) - 1,
    col: columnIndexFromLetter(colLetters) - 1,
  };
}

/** Resolve a `SheetImage.cell` into the 0-based `{ row, col }` ExcelJS's `tl` anchor expects. */
function resolveCellAnchor(cell: SheetImage['cell']): { row: number; col: number } {
  if (typeof cell === 'string') return parseCellString(cell);
  return { row: cell.row - 1, col: cell.col - 1 };
}

/**
 * Embed a list of images into a worksheet. Each image is registered once
 * on the workbook (`workbook.addImage`) then anchored on the sheet at its
 * target cell (`sheet.addImage`). The target row's height is grown to fit
 * the image — ExcelJS never does this automatically, so an image anchored
 * on a default 15pt row would otherwise visually overflow into the rows
 * below it.
 */
export function writeSheetImages(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  workbook: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sheet: any,
  images: SheetImage[]
): void {
  for (const image of images) {
    const base64 = image.base64.includes(',') ? image.base64.split(',')[1] : image.base64;
    const imageId = workbook.addImage({ base64, extension: 'png' });
    const { row, col } = resolveCellAnchor(image.cell);

    sheet.addImage(imageId, {
      tl: { col, row },
      ext: { width: image.width, height: image.height },
    });

    // `sheet.getRow` is 1-based; `row` (the tl anchor) is 0-based, so the
    // row actually holding the image is `row + 1`.
    const targetRow = sheet.getRow(row + 1);
    const neededHeight = image.height * POINTS_PER_PIXEL;
    targetRow.height = Math.max(targetRow.height ?? 15, neededHeight);
  }
}
